import React, { useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import Box from '@mui/material/Box'
import Container from '@mui/material/Container'
import Paper from '@mui/material/Paper'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import { DashboardContent } from '../Dashboard/DashboardLayout/DashboardLayout.styles'
import MobileHeader from '../../components/MobileHeader/MobileHeader'
import { PageWrapper, colors } from '../../styles/_var'
import { BackdropWrapper, DateRequestInfoWrapper } from '../Dashboard/DateRequests/DateRequests.styles'
import ScoreboardBanner from '../../assets/images/scoreboard_banner.jpg'
import { ActivityBackDrop } from '../Dashboard/PlanActivity/CreateActivity/CreateActivity.styles'
import Text from '../../components/Text'
import { UserInfo } from '../../components/UserInfo/UserInfo'
import { routes } from '../../utils/constants'
import { useFetchScoreboardTableQuery } from '../../services/api'
import { LoadingSkeleton } from '../Dashboard/DateRequests/DateRequestInfo'
import { useSetHeaderTitle } from '../../hooks/hooks'
import { scoreboardParticipantToUser, setPageTitle } from '../../utils/helpers'
import type { User } from '../../utils/types'

const PLAYER_NAME_MAX_LENGTH = 7

const truncatePlayerName = (name: string) => name.length > PLAYER_NAME_MAX_LENGTH ? `${name.slice(0, PLAYER_NAME_MAX_LENGTH)}..` : name

const ScoreboardTable = () => {
  const { scoreboard_id, slug } = useParams<{ scoreboard_id: string; slug: string }>()
  const navigate = useNavigate()
  const parsedId = Number(scoreboard_id)
  const skip = !scoreboard_id || !Number.isFinite(parsedId) || parsedId <= 0

  const { data, isLoading, isError } = useFetchScoreboardTableQuery({ id: parsedId }, { skip })

  const title = data?.scoreboard_info?.title ?? 'Scoreboard'
  useSetHeaderTitle(title, true)
  setPageTitle(`${title} — table`)

  const participants = useMemo(() => data?.scoreboard_info?.scoreboard_participants ?? [], [data])

  const userForUsername = useMemo(() => {
    return (username: string): User => {
      const p = participants.find((sp) => sp.username === username)
      const user = p
        ? scoreboardParticipantToUser(p)
        : ({ full_name: username, username, avatar: '' } as User)
      const firstName = (user.full_name || user.username || '').split(' ')[0]
      return { ...user, full_name: truncatePlayerName(firstName) }
    }
  }, [participants])

  const onNavigateBack = () => {
    if (scoreboard_id && slug) {
      navigate(
        routes.SCOREBOARD_DETAIL.replace(':scoreboard_id', scoreboard_id).replace(':slug', slug)
      )
    } else {
      navigate(-1)
    }
  }

  if (!scoreboard_id || !slug) return null

  const rows = data?.scoreboard ?? null
  const totalWeeks = data?.total_weeks ?? 0
  const info = data?.scoreboard_info

  if (skip) return null

  return (
    <DashboardContent isMobile>
      <MobileHeader style={{ display: 'block' }} onNavigateBack={onNavigateBack} />
      <PageWrapper>
        <BackdropWrapper>
          <ActivityBackDrop backdrop={ScoreboardBanner} />
        </BackdropWrapper>
        <DateRequestInfoWrapper style={{ top: -65 }}>
          <Container>
            <Paper sx={{ marginBottom: 3 }}>
              <Box p={2}>
                <Text useDiv variant="medium" weight="bold" mb={1}>
                  {info?.title ?? 'Scoreboard'}
                </Text>
                {!!info?.description && (
                  <Text useDiv variant="medium" mb={1}>
                    {info.description}
                  </Text>
                )}
              </Box>
            </Paper>
            <Box>
              {isLoading && (
                <Box mt={2} p={3}>
                  <LoadingSkeleton />
                </Box>
              )}
              {!isLoading && isError && (
                <Box mt={2} p={3} display="flex" justifyContent="center">
                  <Text color={colors.danger}>Failed to load scoreboard table</Text>
                </Box>
              )}
              {!isLoading && !isError && !rows?.length && (
                <Box p={2}>
                  <Text variant="medium" color="text.secondary" useDiv>
                    {totalWeeks === 0
                      ? 'No scoreboard results yet.'
                      : 'No solo aggregate scores to show yet (team-only or empty results).'}
                  </Text>
                </Box>
              )}
              {!isLoading && !isError && !!rows?.length && (
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell />
                      <TableCell>
                        <Text weight="semibold">Player</Text>
                      </TableCell>
                      <TableCell>
                        <Text weight="semibold" center>Games</Text>
                      </TableCell>
                      <TableCell>
                        <Text weight="semibold" center>Diff</Text>
                      </TableCell>
                      <TableCell>
                        <Text weight="semibold" center>Wins</Text>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map(([username, scoreboardData], index) => (
                      <TableRow key={`${username}-${index}`}>
                        <TableCell>
                          <Text center>{index + 1}</Text>
                        </TableCell>
                        <TableCell>
                          <UserInfo
                            onlyShowName
                            onlyFirstName
                            useSmallAvatar
                            titleTextVariant="regular"
                            user={userForUsername(username)}
                          />
                        </TableCell>
                        <TableCell>
                          <Text center>{totalWeeks}</Text>
                        </TableCell>
                        <TableCell>
                          <Text center>{scoreboardData.points}</Text>
                        </TableCell>
                        <TableCell>
                          <Text weight="bold" center>{scoreboardData.wins}</Text>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>
          </Container>
        </DateRequestInfoWrapper>
      </PageWrapper>
    </DashboardContent>
  )
}

export default ScoreboardTable
